import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ConsultHero from '@/pages/gmb-audit/consult/components/ConsultHero';
import ConsultContextCard from '@/pages/gmb-audit/consult/components/ConsultContextCard';
import ConsultForm from '@/pages/gmb-audit/consult/components/ConsultForm';
import {
  readConsultContext,
  saveConsultContext,
  type ConsultReportContext,
} from '@/pages/gmb-audit/consult/utils/consultContext';

/**
 * Google 商家健診｜預約諮詢（V1 Conversion-1）。
 * 純 Lead Capture + LINE Contact，不是 Calendar Booking。
 * context 由 React Router navigation state 帶入；refresh fallback 只讀 sessionStorage，
 * 絕不將 reportToken / historyScopeToken / internal audit id 放入 URL。
 */
export default function GmbAuditConsultPage() {
  const location = useLocation();

  const [context] = useState<ConsultReportContext | null>(() => {
    const fromState = (location.state as { consultContext?: ConsultReportContext } | null)
      ?.consultContext;
    if (fromState) {
      // 進站即寫入 sessionStorage，供重新整理後 fallback（僅最少必要欄位）。
      saveConsultContext(fromState);
      return fromState;
    }
    return readConsultContext();
  });

  const formRef = useRef<HTMLDivElement | null>(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 頁面 SEO metadata（SPA 於前端設定，不改 index.html）。
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Google 商家健診預約諮詢｜天地人網';

    const meta = document.querySelector('meta[name="description"]');
    const previousDesc = meta?.getAttribute('content') ?? null;
    if (meta) {
      meta.setAttribute(
        'content',
        '完成 Google 商家快速健診後，可向天地人網提出諮詢需求，由顧問協助釐清 Google 商家、網站、LINE、內容曝光與數位接單的優先改善方向。',
      );
    }

    return () => {
      document.title = previousTitle;
      if (meta && previousDesc !== null) meta.setAttribute('content', previousDesc);
    };
  }, []);

  return (
    <SiteLayout>
      <ConsultHero onPrimaryClick={scrollToForm} />

      <section className="ds-container py-12 md:py-16">
        <div className="mx-auto flex max-w-search flex-col gap-8">
          {context && <ConsultContextCard context={context} />}

          <div ref={formRef} className="scroll-mt-24">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">填寫諮詢需求</h2>
              <p className="mt-2 text-base leading-relaxed text-foreground-600">
                留下聯絡方式與目前最想改善的問題，天地人網會依你的商家現況，協助你判斷適合優先處理的方向。
              </p>
            </div>

            <ConsultForm defaultStoreName={context?.displayName ?? ''} />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}